import { useMemo, type ReactNode } from 'react'
import { OpeningType } from '~/components/ThreeDModel/furnitureConfig'
import type { WardrobeColumnConfiguration } from '~/types/wardrobeConfigurationTypes'
import { usePricingQuoteSync } from '~/utils/pricingQuote'
import { computeWardrobeParts } from '~/utils/wardrobeParts'
import type { WardrobeParts } from '~/utils/wardrobeParts'
import { QuoteProvider } from '~/context/pricingQuoteContext'

// Re-exported so existing `~/context/wardrobePartsContext` imports keep working.
export type { PanelPart, WardrobeParts } from '~/utils/wardrobeParts'

interface WardrobePartsProviderProps {
  sku: string
  chipboardId?: string
  width: number
  height: number
  depth: number
  plintHeight: number
  columns: number
  columnWidths: number[]
  openingType: OpeningType
  isMirrored: boolean
  columnConfigurations?: WardrobeColumnConfiguration[]
  children: ReactNode
}


export function WardrobePartsProvider({
  sku,
  chipboardId,
  width,
  height,
  depth,
  plintHeight,
  columns,
  columnWidths,
  openingType,
  isMirrored,
  columnConfigurations,
  children,
}: WardrobePartsProviderProps) {
  const parts = useMemo<WardrobeParts>(
    () =>
      computeWardrobeParts({
        width,
        height,
        depth,
        plintHeight,
        columns,
        columnWidths,
        openingType,
        isMirrored,
        columnConfigurations,
      }),
    [
      width,
      height,
      depth,
      plintHeight,
      columns,
      columnWidths,
      openingType,
      isMirrored,
      columnConfigurations,
    ]
  )

  // Fire a backend pricing-quote on every config change; expose the response.
  const quote = usePricingQuoteSync({
    sku,
    chipboardId,
    width,
    height,
    depth,
    handleType: openingType,
    doorsNumber: parts.doors.reduce(
      (n, d) => n + (d.isHandle ? 0 : d.quantity),
      0
    ),
    sections: columns,
    parts,
    columnConfigurations,
    columnWidths,
  })

  return <QuoteProvider value={quote}>{children}</QuoteProvider>
}
